import React, { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import { get, post } from "../../services/api";
import Select from "react-select";
import { toast } from "react-toastify";
import AttendanceNotificationModal from "./AttendanceNotificationModal";
import AttendanceCalendarModal from "./AttendanceCalendarModal";
import NotesStudentModal from "./NotesStudentModal";
import { CustomValueContainer, handleSelectAll } from "../../utils/selectUtils";
import FullScreenLoader from "../../components/FullScreenLoader";
import IndividualAttendanceModal from "./IndividualAttendanceModal";

function MarkAttendance() {
  const token = localStorage.getItem("token");
  const organizationId = localStorage.getItem("organizationId");
  const savedCenterId =
    localStorage.getItem("centerId") === "null"
      ? null
      : localStorage.getItem("centerId");

  const {
    register,
    formState: { errors, isSubmitting },
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    trigger,
    reset,
  } = useForm({
    defaultValues: {
      centerId: null,
      classId: [],
      classSectionId: [],
      AttnDate: new Date().toISOString().split("T")[0], // Default to today's date
    },
  });

  const caretStyle = {
    position: "absolute",
    top: "-9px",
    left: "50%",
    transform: "translateX(-50%)",
    width: "0",
    height: "0",
    borderLeft: "7px solid transparent",
    borderRight: "7px solid transparent",
    borderTop: "9px solid red",
    // animation: "pulse 1.5s infinite",
  };

  const attendanceTypeReverseMap = {
    1: "P",
    2: "A",
    3: "O",
    4: "L",
    5: "I",
    6: "S",
    7: "T",
    8: "M",
    9: "B",
    10: "U",
    11: "E",
    12: "D",
  };

  const [loading, setLoading] = useState(false);
  const [attendanceData, setAttendanceData] = useState(null);
  const [fullCurrentMonthDays, setFullCurrentMonthDays] = useState([]);
  const [fullStudents, setFullStudents] = useState([]);
  const [centersData, setCentersData] = useState([]);
  const [classesData, setClassesData] = useState([]);
  const [classSectionsData, setClassSectionsData] = useState([]);
  const [alertNotificationModal, setAlertNotificationModal] = useState(false);
  const [notifyParentId, setNotifyParentId] = useState(0);
  const [notesAgainstStudentId, setNotesAgainstStudentId] = useState(0);
  const [
    notifyStudentFirstRegistrationId,
    setNotifyStudentFirstRegistrationId,
  ] = useState(0);
  const [notesAgainstStudentModal, setNotesAgainstStudentModal] =
    useState(false);
  const [individualAttendanceModal, setIndividualAttendanceModal] =
    useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // State for search term
  const [selectedUserIds, setSelectedUserIds] = useState(new Set()); // Track selected userIds
  const [calendarModalOpen, setCalendarModalOpen] = useState(false); // Calendar modal state
  const [activeDropdown, setActiveDropdown] = useState(null); // Track which day's dropdown is active
  const [columnBulkStatus, setColumnBulkStatus] = useState({}); // Track selected status for each column
  const [viewPeriod, setViewPeriod] = useState("daily"); // State for view period (daily, weekly, monthly)

  const getClassData = async () => {
    try {
      const classesResponse = await get(
        `CenterClass/GetClassCenterByCenterId?id=${savedCenterId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setClassesData(classesResponse || []);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const handleCalendarDateSelect = (selectedDate) => {
    setValue("AttnDate", selectedDate);
    setCalendarModalOpen(false);
    toast.info(`Date selected: ${new Date(selectedDate).toLocaleDateString()}`);
  };

  const fetchClassSectionByCenterClassIdAnd = async (centerClassId) => {
    try {
      const sectionsResponse = await get(
        `ClassSection/GetAllSectionByCenterClassIdAndCenterId?centerClassId=${centerClassId}&centerId=${savedCenterId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return sectionsResponse || [];
    } catch (error) {
      console.error("Error fetching sections:", error);
      return [];
    }
  };

  const fetchAttendanceByClassSectionId = async (
    centerId,
    classSectionId,
    selectedDate
  ) => {
    setLoading(true);
    try {
      const response = await get(
        `Student/GetAllAttnStudentById?centerId=${centerId}&id=${classSectionId}&attnDate=${selectedDate}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response;
    } catch (error) {
      console.error("Error fetching attendance data:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        const response = await get(
          `Dropdown/GetAllCentersByOrganizationId?organizationId=${organizationId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setCentersData(response);

        if (savedCenterId) {
          const selectedCenter = response.find(
            (center) => center.id === parseInt(savedCenterId, 10)
          );

          if (selectedCenter) {
            setValue("centerId", {
              value: selectedCenter.id,
              label: selectedCenter.name,
            });
          }
        }
      } catch (error) {
        console.error("Error fetching centers:", error);
      }
    };

    fetchCenters();
    getClassData();
  }, [savedCenterId, setValue, token, organizationId]);

  const fetchAllAttendance = async () => {
    setLoading(true);
    const centerId = getValues("centerId")?.value;
    const selectedSections = getValues("classSectionId") || [];
    const attnDate =
      getValues("AttnDate") || new Date().toISOString().split("T")[0];

    if (centerId && selectedSections.length > 0) {
      let allStudents = [];
      let currentMonthDays = null;

      for (const sec of selectedSections) {
        try {
          const resp = await fetchAttendanceByClassSectionId(
            centerId,
            sec.value,
            attnDate
          );
          if (resp && resp.students && resp.currentMonthDays) {
            const mappedStudents = resp.students.map((stu) => ({
              ...stu,
              attendance: stu.attendance.map((code) => {
                const num = Number(code);
                return isNaN(num)
                  ? code
                  : attendanceTypeReverseMap[num] || code;
              }),
              sectionId: sec.value,
              sectionName: sec.label || "Unknown Section",
            }));
            allStudents = [...allStudents, ...mappedStudents];
            if (!currentMonthDays) currentMonthDays = resp.currentMonthDays;
          } else {
            console.warn(`Empty response for section ${sec.value}`);
          }
        } catch (error) {
          console.error(
            `Error fetching attendance for section ${sec.value}:`,
            error
          );
        }
      }

      if (!currentMonthDays || !allStudents.length) {
        console.error("No valid data fetched");
        setAttendanceData(null);
        setFullCurrentMonthDays([]);
        setFullStudents([]);
        setSelectedUserIds(new Set());
        setColumnBulkStatus({});
        toast.info("No attendance data available for the selected criteria.");
        return;
      }

      // Sort currentMonthDays by date
      currentMonthDays.sort((a, b) => new Date(a.date) - new Date(b.date));

      setFullCurrentMonthDays(currentMonthDays);
      setFullStudents(allStudents);

      // Find the index of the selected date
      const selectedDateIndex = currentMonthDays.findIndex(
        (day) => day.date === attnDate
      );

      let daysToShow =
        viewPeriod === "daily" ? 1 : viewPeriod === "weekly" ? 7 : 30;
      let filteredDays = [];

      if (selectedDateIndex !== -1) {
        let startIndex = selectedDateIndex - (daysToShow - 1);
        let endIndex = selectedDateIndex + 1;

        if (startIndex < 0) {
          const additionalNeeded = Math.abs(startIndex);
          startIndex = 0;
          endIndex = Math.min(
            endIndex + additionalNeeded,
            currentMonthDays.length
          );
        }

        filteredDays = currentMonthDays.slice(startIndex, endIndex);
      } else {
        // Fallback: Use the last available non-future clickable days
        const nonFutureDays = currentMonthDays.filter(
          (day) => day.isClickable && new Date(day.date) <= new Date()
        );
        filteredDays = nonFutureDays.slice(-daysToShow);
      }

      // If fewer days are available, show all available
      if (filteredDays.length === 0 && currentMonthDays.length > 0) {
        filteredDays = currentMonthDays
          .filter((day) => day.isClickable && new Date(day.date) <= new Date())
          .slice(-1);
      }

      // Align attendance with filtered days from full
      const updatedStudents = allStudents.map((student) => ({
        ...student,
        attendance: filteredDays.map((day) => {
          const dayIndex = currentMonthDays.findIndex(
            (d) => d.date === day.date
          );
          return dayIndex !== -1 ? student.attendance[dayIndex] : "";
        }),
      }));

      setAttendanceData({
        currentMonthDays: filteredDays,
        students: updatedStudents,
      });

      setSelectedUserIds(new Set());
      setColumnBulkStatus({});
    } else {
      setAttendanceData(null);
      setFullCurrentMonthDays([]);
      setFullStudents([]);
      setSelectedUserIds(new Set());
      setColumnBulkStatus({});
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchAllAttendance();
  }, [
    watch("centerId"),
    watch("classSectionId"),
    watch("AttnDate"),
    viewPeriod,
    getValues,
  ]);

  const [editingCell, setEditingCell] = useState({
    studentIndex: null,
    dayIndex: null,
  });

  const todayStr = new Date().toISOString().split("T")[0];

  useEffect(() => {
    setValue("AttnDate", todayStr);
  }, [setValue]);

  // const handleStatusClick = (studentIndex, dayIndex) => {
  //   if (
  //     editingCell.studentIndex === studentIndex &&
  //     editingCell.dayIndex === dayIndex
  //   ) {
  //     setEditingCell({ studentIndex: null, dayIndex: null });
  //   } else {
  //     setEditingCell({ studentIndex, dayIndex });
  //   }
  //   setActiveDropdown(null);
  // };

  const handleStatusClick = (studentIndex, dayIndex) => {
    setEditingCell({ studentIndex, dayIndex });
    setActiveDropdown(null);
  };

  const handleStatusChange = (e, studentIndex, dayIndex) => {
    const newStatus = e.target.value;
    const filteredDay = attendanceData.currentMonthDays[dayIndex];
    const actualDayIndex = fullCurrentMonthDays.findIndex(
      (d) => d.date === filteredDay.date
    );
    const student = attendanceData.students[studentIndex];

    setFullStudents((prev) =>
      prev.map((s) => {
        if (s.userId === student.userId) {
          const newAttendance = [...s.attendance];
          newAttendance[actualDayIndex] = newStatus;
          return { ...s, attendance: newAttendance };
        }
        return s;
      })
    );

    setAttendanceData((prevData) => ({
      ...prevData,
      students: prevData.students.map((s, sIndex) =>
        sIndex === studentIndex
          ? {
              ...s,
              attendance: s.attendance.map((status, dIndex) =>
                dIndex === dayIndex ? newStatus : status
              ),
            }
          : s
      ),
    }));
    setEditingCell({ studentIndex: null, dayIndex: null });
  };

  const handleColumnBulkAttendance = (dayIndex, status) => {
    if (!attendanceData || !attendanceData.students.length) {
      return;
    }

    const filteredDay = attendanceData.currentMonthDays[dayIndex];
    const actualDayIndex = fullCurrentMonthDays.findIndex(
      (d) => d.date === filteredDay.date
    );
    const dayData = attendanceData.currentMonthDays[dayIndex];
    const today = new Date();
    const dayDateObj = new Date(dayData.date);

    // Remove the today-only restriction
    if (
      !dayData.isClickable ||
      dayDateObj > today ||
      dayData.isHoliday ||
      dayData.isWeekend
    ) {
      return;
    }

    setFullStudents((prev) =>
      prev.map((s) => {
        const newAttendance = [...s.attendance];
        newAttendance[actualDayIndex] = status;
        return { ...s, attendance: newAttendance };
      })
    );

    setAttendanceData((prevData) => ({
      ...prevData,
      students: prevData.students.map((student) => ({
        ...student,
        attendance: student.attendance.map((currentStatus, dIndex) =>
          dIndex === dayIndex ? status : currentStatus
        ),
      })),
    }));
    setColumnBulkStatus((prev) => ({ ...prev, [dayIndex]: status }));
  };

  const handleColumnBulkActionChange = (dayIndex, e) => {
    const status = e.target.value;
    if (status) {
      handleColumnBulkAttendance(dayIndex, status);
      setActiveDropdown(null);
    }
  };

  const toggleDropdown = (dayIndex) => {
    setActiveDropdown(activeDropdown === dayIndex ? null : dayIndex);
    setEditingCell({ studentIndex: null, dayIndex: null });
  };

  const getAttendanceLabel = (code) => {
    const labels = {
      P: "Present",
      A: "Absent (Authorized)",
      O: "Absent (Not Authorized)",
      L: "Late (Unauthorized)",
      I: "Late (Authorized)",
      S: "Absent (Approved - Educational Activity)",
      T: "Absent (Travel)",
      M: "Absent (Medical)",
      B: "Early Dismissal 1:30-3:30 Unauthorized",
      U: "Early Dismissal 1:30-3:30 Authorized",
      E: "Early Dismissal 11:30-1:30 Unauthorized",
      D: "Early Dismissal 11:30-1:30 Authorized",
    };
    return labels[code] || code;
  };

  const NotifyStudent = async (id, StudentFirstRegistrationId) => {
    setNotifyParentId(id);
    setNotifyStudentFirstRegistrationId(StudentFirstRegistrationId);
    setAlertNotificationModal(true);
  };

  const NotesAgainstStudent = async (id) => {
    setNotesAgainstStudentId(id);
    setNotesAgainstStudentModal(true);
  };

  const handleNotifyAllSelected = () => {
    const selectedIds = Array.from(selectedUserIds).join(",");
    if (selectedIds) {
      setNotifyParentId(selectedIds);
      setAlertNotificationModal(true);
    } else {
      toast.error("Please select at least one student.");
    }
  };

  const handleCheckboxChange = (userId) => {
    setSelectedUserIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAllChange = (e) => {
    const isChecked = e.target.checked;
    const newSelected = new Set(selectedUserIds);
    if (isChecked) {
      filteredAttendanceData.forEach((student) => {
        newSelected.add(student.userId);
      });
    } else {
      filteredAttendanceData.forEach((student) => {
        newSelected.delete(student.userId);
      });
    }
    setSelectedUserIds(newSelected);
  };

  // const saveData = async (data) => {
  //   if (!token) {
  //     console.error("Unauthorized.");
  //     return;
  //   }

  //   const createdBy = localStorage.getItem("userId");
  //   const updatedBy = localStorage.getItem("userId");
  //   const selectedSections = getValues("classSectionId") || [];

  //   if (selectedSections.length === 0) {
  //     toast.error("Please select at least one section.");
  //     return;
  //   }

  //   try {
  //     // Create attendance data for all sections at once
  //     const attendancePayload = selectedSections.map((sec) => {
  //       const sectionStudents = fullStudents.filter(
  //         (stu) => stu.sectionId === sec.value
  //       );

  //       // Create a deep copy of students to prevent mutation issues
  //       const studentsWithAttendance = sectionStudents.map((student) => ({
  //         ...student,
  //         // Ensure you're capturing the current attendance state
  //         // Replace 'attendanceType' with your actual attendance property name
  //         // attendanceType: student.attendanceType || "A", // default to 'A' if not set
  //       }));

  //       return {
  //         createdBy,
  //         updatedBy,
  //         classSectionId: sec.value,
  //         attendanceData: {
  //           currentMonthDays: [...fullCurrentMonthDays], // shallow copy of array
  //           students: studentsWithAttendance.map(
  //             ({ sectionId, sectionName, ...stu }) => stu
  //           ),
  //         },
  //       };
  //     });
  //     const response = await post(
  //       "Student/AddUpdateStdAttn",
  //       attendancePayload,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //       }
  //     );

  //     if (response) {
  //       toast.success(response.message || "Attendance saved successfully!");
  //     }
  //   } catch (error) {
  //     console.error("Error saving attendance:", error);
  //     toast.error("Failed to save attendance.");
  //   }
  // };

  const saveData = async (data) => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    const createdBy = localStorage.getItem("userId");
    const updatedBy = localStorage.getItem("userId");
    const selectedSections = getValues("classSectionId") || [];

    if (selectedSections.length === 0) {
      toast.error("Please select at least one section.");
      return;
    }

    try {
      for (const sec of selectedSections) {
        const sectionStudents = fullStudents.filter(
          (stu) => stu.sectionId === sec.value
        );
        const sectionAttendanceData = {
          currentMonthDays: fullCurrentMonthDays,
          students: sectionStudents.map(
            ({ sectionId, sectionName, ...stu }) => stu
          ),
        };

        const formData = {
          createdBy,
          updatedBy,
          classSectionId: sec.value,
          attendanceData: sectionAttendanceData,
        };

        const response = await post("Student/AddUpdateStdAttn", formData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        if (response && response.message) {
          // You might want to capture the message from the last response or a generic one
        }
      }
      toast.success("Success!");
    } catch (error) {
      console.error("Error saving attendance:", error);
      toast.error("Failed to save attendance.");
    }
  };

  const filteredAttendanceData =
    attendanceData?.students?.filter((student) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        student.rollNo.toLowerCase().includes(searchLower) ||
        student.sectionName.toLowerCase().includes(searchLower) ||
        student.name.toLowerCase().includes(searchLower)
      );
    }) || [];

  const allOption = { value: "*", label: "Select All" };
  const centerClassOptions = useMemo(() => {
    return classesData.length > 0
      ? [
          allOption,
          ...classesData.map((classItem) => ({
            value: classItem.id,
            label: classItem.name,
          })),
        ]
      : [];
  }, [classesData]);

  const classSectionOptions = useMemo(() => {
    return classSectionsData.length > 0
      ? [
          allOption,
          ...classSectionsData.map((section) => ({
            value: section.id,
            label: section.name,
          })),
        ]
      : [];
  }, [classSectionsData]);

  const viewAttendance = async (student) => {
    setSelectedStudent(student);
    setIndividualAttendanceModal(true);
  };

  return (
    <>
      <div className="page-header d-flex justify-content-between align-items-center mb-3">
        <h2 className="page-heading fs-7 mb-0">Mark Attendance</h2>
        <div className="d-flex align-items-center gap-2">
          <div className="" style={{ width: "180px" }}>
            <div className="">
              <input
                type="text"
                className="form-control py-2"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <button
            type="button"
            className="btn btn-outline-primary btn-sm py-2 px-3"
            onClick={() => setCalendarModalOpen(true)}
            data-tooltip-id="global-tooltip"
            data-tooltip-content="Academic Calendar"
          >
            <i className="fa fa-calendar-days"></i>
          </button>
        </div>
      </div>

      <form className="new-form-style" onSubmit={handleSubmit(saveData)}>
        <div className="row g-3">
          <div className="col-md-3 d-none">
            <label className="form-label form-label-new">
              Center<span className="text-danger fs-9">*</span>
            </label>
            <Controller
              name="centerId"
              control={control}
              rules={{ required: "This field is required." }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={centersData.map((center) => ({
                    value: center.id,
                    label: center.name,
                  }))}
                  isDisabled={savedCenterId !== "0"}
                  onChange={(selectedOption, actionMeta) => {
                    if (actionMeta.action === "clear") {
                      setValue("centerId", null);
                      setValue("classId", []);
                      setValue("classSectionId", []);
                      setClassSectionsData([]);
                      setAttendanceData(null);
                    } else {
                      setValue("centerId", selectedOption);
                    }
                    trigger("centerId");
                  }}
                  className="react-select-style"
                  classNamePrefix="react-select-style-pref"
                  isClearable
                />
              )}
            />
            {errors.centerId && (
              <small className="text-danger">{errors.centerId.message}</small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Class<span className="text-danger fs-9">*</span>
            </label>
            <Controller
              name="classId"
              control={control}
              rules={{ required: "This field is required." }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={classesData.map((classItem) => ({
                    value: classItem.id,
                    label: classItem.name,
                  }))}
                  isMulti
                  // closeMenuOnSelect={false}
                  onChange={async (selectedOptions, actionMeta) => {
                    if (actionMeta.action === "clear") {
                      setValue("classId", []);
                      setClassSectionsData([]);
                      setValue("classSectionId", []);
                      setAttendanceData(null);
                    } else {
                      setValue("classId", selectedOptions);
                      let allSections = [];
                      for (const cls of selectedOptions) {
                        const sections =
                          await fetchClassSectionByCenterClassIdAnd(cls.value);
                        const options = sections.map((sec) => ({
                          value: sec.id,
                          label: `${cls.label} - ${sec.name}`,
                        }));
                        allSections = [...allSections, ...options];
                      }
                      setClassSectionsData(allSections);
                      setValue("classSectionId", []);
                    }
                    trigger("classId");
                  }}
                  className="react-select-style"
                  classNamePrefix="react-select-style-pref"
                  isClearable
                />
              )}
            />
            {errors.classId && (
              <small className="text-danger">{errors.classId.message}</small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Class - Section
              <span className="text-danger fs-9">*</span>
            </label>
            <Controller
              name="classSectionId"
              control={control}
              rules={{ required: "This field is required." }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={classSectionsData}
                  isMulti
                  // closeMenuOnSelect={false}
                  onChange={(selectedOptions, actionMeta) => {
                    if (actionMeta.action === "clear") {
                      setValue("classSectionId", []);
                      setAttendanceData(null);
                    } else {
                      setValue("classSectionId", selectedOptions);
                    }
                    trigger("classSectionId");
                  }}
                  className="react-select-style"
                  classNamePrefix="react-select-style-pref"
                  isClearable
                />
              )}
            />
            {errors.classSectionId && (
              <small className="text-danger">
                {errors.classSectionId.message}
              </small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">
              Date<span className="text-danger fs-9">*</span>
            </label>
            <input
              {...register("AttnDate", {
                required: "This field is required.",
              })}
              type="date"
              className="form-control"
              placeholder=""
              // max={
              //   new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
              //     .toISOString()
              //     .split("T")[0]
              // }
            />
            {errors.AttnDate && (
              <small className="text-danger">{errors.AttnDate.message}</small>
            )}
          </div>
          <div className="col-md-3">
            <label className="form-label form-label-new">View Period</label>
            <select
              className="form-select"
              value={viewPeriod}
              onChange={(e) => setViewPeriod(e.target.value)}
            >
              <option value="daily">Daily (1 Day)</option>
              <option value="weekly">Weekly (7 Days)</option>
              <option value="monthly">Monthly (30 Days)</option>
            </select>
          </div>
        </div>

        <div className="card mt-3">
          <div className="card-body att-min-h">
            {loading ? (
              <FullScreenLoader />
            ) : attendanceData ? (
              <>
                <div className="att-scrollable-area">
                  <table className="table table-bordered custom-table attendance-table bg-white mt-2">
                    <thead
                      style={{
                        position: "sticky",
                        top: "0",
                        backgroundColor: "#fff",
                        zIndex: "1",
                      }}
                    >
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            onChange={handleSelectAllChange}
                            checked={
                              filteredAttendanceData.length > 0 &&
                              filteredAttendanceData.every((student) =>
                                selectedUserIds.has(student.userId)
                              )
                            }
                          />
                        </th>
                        <th>Sr No.</th>
                        <th colSpan={2}>Student</th>
                        <th>
                          <div className="days-row d-flex gap-1">
                            {attendanceData?.currentMonthDays?.map(
                              (day, dayIndex) => {
                                const today = new Date();
                                const dayDate = new Date(day.date);
                                const isDisabled =
                                  !day.isClickable ||
                                  dayDate > today ||
                                  day.isHoliday ||
                                  day.isWeekend;
                                const currentStatus =
                                  columnBulkStatus[dayIndex] || "P";
                                const isCurrentDate = day.date === todayStr;

                                return (
                                  <div
                                    className="day-box position-relative"
                                    key={day.date}
                                    data-tooltip-id="global-tooltip"
                                    data-tooltip-content="Mark for All Students"
                                  >
                                    <div
                                      className={`mark-box ${
                                        isDisabled
                                          ? "disabled"
                                          : currentStatus === "P"
                                          ? "present"
                                          : currentStatus === "L"
                                          ? "late-unauthorized"
                                          : currentStatus === "I"
                                          ? "late-authorized"
                                          : currentStatus === "S"
                                          ? "absent-approved"
                                          : currentStatus === "T"
                                          ? "absent-travel"
                                          : currentStatus === "M"
                                          ? "absent-medical"
                                          : currentStatus === "O"
                                          ? "absent-not-authorized"
                                          : currentStatus === "A"
                                          ? "absent-authorized"
                                          : currentStatus === "B"
                                          ? "early-dismissal-unauthorized"
                                          : currentStatus === "U"
                                          ? "early-dismissal-authorized"
                                          : currentStatus === "E"
                                          ? "early-dismissal-unauthorized"
                                          : currentStatus === "D"
                                          ? "early-dismissal-authorized"
                                          : ""
                                      }`}
                                      onClick={(e) => {
                                        if (
                                          e.target.tagName !== "SELECT" &&
                                          !isDisabled
                                        ) {
                                          toggleDropdown(dayIndex);
                                        }
                                      }}
                                      style={{
                                        cursor: !isDisabled
                                          ? "pointer"
                                          : "not-allowed",
                                      }}
                                    >
                                      {activeDropdown === dayIndex &&
                                      !isDisabled ? (
                                        <select
                                          className="att-types-dropdown"
                                          onChange={(e) =>
                                            handleColumnBulkActionChange(
                                              dayIndex,
                                              e
                                            )
                                          }
                                          defaultValue=""
                                          autoFocus
                                          onClick={(e) => e.stopPropagation()}
                                          onMouseDown={(e) =>
                                            e.stopPropagation()
                                          }
                                        >
                                          <option value="">Select</option>
                                          <option value="P">
                                            Present (Virtual)
                                          </option>
                                          <option value="L">
                                            Late (Unauthorized)
                                          </option>
                                          <option value="I">
                                            Late (Authorized)
                                          </option>
                                          <option value="S">
                                            Absent (Approved - Educational
                                            Activity)
                                          </option>
                                          <option value="T">
                                            Absent (Travel)
                                          </option>
                                          <option value="M">
                                            Absent (Medical)
                                          </option>
                                          <option value="O">
                                            Absent (Not Authorized)
                                          </option>
                                          <option value="A">
                                            Absent (Authorized - Other)
                                          </option>
                                          <option value="B">
                                            Early Dismissal 1:30-3:30
                                            Unauthorized
                                          </option>
                                          <option value="U">
                                            Early Dismissal 1:30-3:30 Authorized
                                          </option>
                                          <option value="E">
                                            Early Dismissal 11:30-1:30
                                            Unauthorized
                                          </option>
                                          <option value="D">
                                            Early Dismissal 11:30-1:30
                                            Authorized
                                          </option>
                                        </select>
                                      ) : (
                                        currentStatus
                                      )}
                                    </div>
                                    {day.dayNumber} <br />
                                    <small>{day.dayName}</small>
                                    {isCurrentDate && (
                                      <span
                                        className="caret-highlight"
                                        style={caretStyle}
                                      ></span>
                                    )}
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </th>
                        <th></th>
                        {/* <th></th>
                        <th>
                          <i
                            className="fas fa-bell"
                            onClick={handleNotifyAllSelected}
                            style={{ cursor: "pointer", color: "blue" }}
                            data-tooltip-id="global-tooltip"
                            data-tooltip-content="Notify All Selected"
                          ></i>
                        </th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredAttendanceData.map((student, studentIndex) => (
                        <tr key={studentIndex}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedUserIds.has(student.userId)}
                              onChange={() =>
                                handleCheckboxChange(student.userId)
                              }
                            />
                          </td>
                          <td>{studentIndex + 1}</td>
                          <td colSpan={2}>
                            <div className="d-flex align-items-center gap-2">
                              <div className="table-listing-image-preview">
                                <img
                                  src={
                                    !student.imagePath ||
                                    student.imagePath === "" ||
                                    student.imagePath === "string"
                                      ? `${process.env.PUBLIC_URL}/image/team/avatar-placeholder.jpg`
                                      : `${
                                          process.env.REACT_APP_LIVE_DOMAIN_URL
                                        }/ExternalImages/${student.imagePath.replace(
                                          /^\/+/,
                                          ""
                                        )}`
                                  }
                                  alt={student.name}
                                  onError={(e) => {
                                    e.target.src = `${process.env.PUBLIC_URL}/image/team/avatar-placeholder.jpg`;
                                  }}
                                />
                              </div>
                              {student.rollNo +
                                " - " +
                                student.name +
                                " " +
                                student.sectionName}
                            </div>
                          </td>
                          <td style={{ width: "65%" }}>
                            <div className="mark-boxes-row d-flex gap-1">
                              {student.attendance.map((day, dayIndex) => {
                                const dayData =
                                  attendanceData.currentMonthDays[dayIndex];
                                const today = new Date();
                                const dayDate = new Date(dayData.date);

                                const isDisabled =
                                  !dayData.isClickable ||
                                  dayDate > today ||
                                  day === "H" ||
                                  dayData.isHoliday ||
                                  dayData.isWeekend;
                                const isCurrentDate = dayData.date === todayStr;

                                return (
                                  <div
                                    key={dayIndex}
                                    className={`mark-box ${
                                      day === "P"
                                        ? "present"
                                        : day === "L"
                                        ? "late-unauthorized"
                                        : day === "I"
                                        ? "late-authorized"
                                        : day === "S"
                                        ? "absent-approved"
                                        : day === "T"
                                        ? "absent-travel"
                                        : day === "M"
                                        ? "absent-medical"
                                        : day === "O"
                                        ? "absent-not-authorized"
                                        : day === "A"
                                        ? "absent-authorized"
                                        : day === "B"
                                        ? "early-dismissal-unauthorized"
                                        : day === "U"
                                        ? "early-dismissal-authorized"
                                        : day === "E"
                                        ? "early-dismissal-unauthorized"
                                        : day === "D"
                                        ? "early-dismissal-authorized"
                                        : ""
                                    } ${isDisabled ? "disabled" : ""}`}
                                    onClick={() =>
                                      !isDisabled &&
                                      handleStatusClick(studentIndex, dayIndex)
                                    }
                                    style={{
                                      cursor: !isDisabled
                                        ? "pointer"
                                        : "not-allowed",
                                    }}
                                  >
                                    {editingCell.studentIndex ===
                                      studentIndex &&
                                    editingCell.dayIndex === dayIndex ? (
                                      <select
                                        className="att-types-dropdown"
                                        value={day}
                                        onChange={(e) =>
                                          handleStatusChange(
                                            e,
                                            studentIndex,
                                            dayIndex
                                          )
                                        }
                                        disabled={isDisabled}
                                      >
                                        <option value="">Select</option>
                                        <option value="P">
                                          Present (Virtual)
                                        </option>
                                        <option value="L">
                                          Late (Unauthorized)
                                        </option>
                                        <option value="I">
                                          Late (Authorized)
                                        </option>
                                        <option value="S">
                                          Absent (Approved - Educational
                                          Activity)
                                        </option>
                                        <option value="T">
                                          Absent (Travel)
                                        </option>
                                        <option value="M">
                                          Absent (Medical)
                                        </option>
                                        <option value="O">
                                          Absent (Not Authorized)
                                        </option>
                                        <option value="A">
                                          Absent (Authorized - Other)
                                        </option>
                                        <option value="B">
                                          Early Dismissal 1:30-3:30 Unauthorized
                                        </option>
                                        <option value="U">
                                          Early Dismissal 1:30-3:30 Authorized
                                        </option>
                                        <option value="E">
                                          Early Dismissal 11:30-1:30
                                          Unauthorized
                                        </option>
                                        <option value="D">
                                          Early Dismissal 11:30-1:30 Authorized
                                        </option>
                                      </select>
                                    ) : (
                                      day || ""
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </td>
                          <td>
                            <i
                              className="fas fa-calendar-days"
                              onClick={() => viewAttendance(student)}
                              style={{ cursor: "pointer" }}
                              data-tooltip-id="global-tooltip"
                              data-tooltip-content="Attendance Report"
                            >
                              {student.isAttnNotes}
                            </i>
                          </td>
                           <td>
                            <i
                              className={
                                student.isAttnNotes
                                  ? "fas fa-message"
                                  : "fa-regular fa-message"
                              }
                              onClick={() =>
                                NotesAgainstStudent(
                                  student.rollNo,
                                  student.StudentFirstRegistrationId
                                )
                              }
                              style={{ cursor: "pointer" }}
                              data-tooltip-id="global-tooltip"
                              data-tooltip-content="Add Notes"
                            >
                              {student.isAttnNotes}
                            </i>
                          </td>
                          {/* <td>
                            <i
                              className="fas fa-bell"
                              onClick={() =>
                                NotifyStudent(
                                  student.userId,
                                  student.studentFirstRegistrationId
                                )
                              }
                              style={{ cursor: "pointer" }}
                              data-tooltip-id="global-tooltip"
                              data-tooltip-content="Notify Parent"
                            ></i>
                          </td> */}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  <div className="d-flex justify-content-center">
                    <div className="attendance-symbols">
                      <div className="symbol">
                        <div className="mark-box present">P</div> —
                        <div className="symbol-desc">Present (Virtual)</div>
                      </div>
                      <div className="symbol">
                        <div className="mark-box late-unauthorized">L</div> —
                        <div className="symbol-desc">Late (Unauthorized)</div>
                      </div>
                      <div className="symbol">
                        <div className="mark-box late-authorized">I</div> —
                        <div className="symbol-desc">Late (Authorized)</div>
                      </div>
                      <div className="symbol">
                        <div className="mark-box absent-approved">S</div> —
                        <div className="symbol-desc">
                          Absent (Approved - Educational Activity)
                        </div>
                      </div>
                      <div className="symbol">
                        <div className="mark-box absent-travel">T</div> —
                        <div className="symbol-desc">Absent (Travel)</div>
                      </div>
                      <div className="symbol">
                        <div className="mark-box absent-medical">M</div> —
                        <div className="symbol-desc">Absent (Medical)</div>
                      </div>
                      <div className="symbol">
                        <div className="mark-box absent-not-authorized">O</div>{" "}
                        —
                        <div className="symbol-desc">
                          Absent (Not Authorized)
                        </div>
                      </div>
                      <div className="symbol">
                        <div className="mark-box absent-authorized">A</div> —
                        <div className="symbol-desc">
                          Absent (Authorized - Other)
                        </div>
                      </div>
                      <div className="symbol">
                        <div className="mark-box early-dismissal-unauthorized">
                          B
                        </div>{" "}
                        —
                        <div className="symbol-desc">
                          Early Dismissal 1:30-3:30 Unauthorized
                        </div>
                      </div>
                      <div className="symbol">
                        <div className="mark-box early-dismissal-authorized">
                          U
                        </div>{" "}
                        —
                        <div className="symbol-desc">
                          Early Dismissal 1:30-3:30 Authorized
                        </div>
                      </div>
                      <div className="symbol">
                        <div className="mark-box early-dismissal-unauthorized">
                          E
                        </div>{" "}
                        —
                        <div className="symbol-desc">
                          Early Dismissal 11:30-1:30 Unauthorized
                        </div>
                      </div>
                      <div className="symbol">
                        <div className="mark-box early-dismissal-authorized">
                          D
                        </div>{" "}
                        —
                        <div className="symbol-desc">
                          Early Dismissal 11:30-1:30 Authorized
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-3">
                  <button
                    className="btn btn-primary btn-sm"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Saving..." : "Save"}
                  </button>
                </div>
              </>
            ) : (
              <div className="">
                <div className="text-center">
                  There are no records to display
                </div>
              </div>
            )}
          </div>
        </div>
      </form>

      {alertNotificationModal && (
        <AttendanceNotificationModal
          onClose={() => setAlertNotificationModal(false)}
          parentId={notifyParentId}
          StudentFirstRegistrationId={notifyStudentFirstRegistrationId}
        />
      )}
      {notesAgainstStudentModal && (
        <NotesStudentModal
          onClose={() => {
            setNotesAgainstStudentModal(false);
          }}
          studentId={notesAgainstStudentId}
          onSave={(notes) => {
            const hasNotes = notes && notes.trim().length > 0;
            setFullStudents((prev) =>
              prev.map((s) =>
                s.rollNo === notesAgainstStudentId
                  ? { ...s, isAttnNotes: hasNotes }
                  : s
              )
            );
            setAttendanceData((prev) => ({
              ...prev,
              students: prev.students.map((s) =>
                s.rollNo === notesAgainstStudentId
                  ? { ...s, isAttnNotes: hasNotes }
                  : s
              ),
            }));
          }}
        />
      )}

      {individualAttendanceModal && (
        <IndividualAttendanceModal
          onClose={() => {
            setIndividualAttendanceModal(false);
          }}
          student={selectedStudent}
        />
      )}

      <AttendanceCalendarModal
        isOpen={calendarModalOpen}
        onClose={() => setCalendarModalOpen(false)}
        centerId={getValues("centerId")?.value || savedCenterId}
        selectedDate={getValues("AttnDate")}
        onDateSelect={handleCalendarDateSelect}
      />
    </>
  );
}

export default MarkAttendance;
